/**
 * @fileoverview Data fetching hook for Tech Stack Analysis dashboard
 * @module TechStackAnalysis/hooks/useTechStack
 */

import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../lib/apiClient';
import logger from '../../../lib/logger';
import { DEFAULT_PERIOD } from '../../UnifiedTimeFilter';

// Domain category mapping - maps tech component categories to domains
const DOMAIN_MAPPING = {
  'Network': ['Networking', 'Network Equipment', 'Network Services', 'Network Infrastructure'],
  'Security': ['Security', 'Cybersecurity', 'Identity & Access', 'Identity Management', 'Access Management'],
  'Cloud': ['Cloud Infrastructure', 'Cloud Services', 'IaaS', 'PaaS', 'Cloud', 'SaaS'],
  'Data': ['Data & Analytics', 'Database', 'Business Intelligence', 'Analytics', 'Data Management'],
  'Data Center': ['Data Center', 'Hardware', 'Infrastructure', 'Servers'],
  'Collaboration': ['Collaboration', 'Productivity', 'Communication', 'Unified Communications'],
  'Contact Center': ['Contact Center', 'Customer Service', 'CX', 'Call Center'],
  'Applications': ['Applications', 'Enterprise Software', 'Software', 'Business Applications'],
  'Endpoint': ['Endpoint', 'Devices', 'Hardware', 'Mobile', 'Desktop', 'Endpoint Management']
};

// Domain icons and colors
const DOMAIN_CONFIG = {
  'Network': { icon: 'Network', color: '#06b6d4', code: 'NET' },
  'Security': { icon: 'Shield', color: '#ef4444', code: 'SEC' },
  'Cloud': { icon: 'Cloud', color: '#3b82f6', code: 'CLD' },
  'Data': { icon: 'Database', color: '#8b5cf6', code: 'DAT' },
  'Data Center': { icon: 'Server', color: '#f59e0b', code: 'DC' },
  'Collaboration': { icon: 'Users', color: '#10b981', code: 'COL' },
  'Contact Center': { icon: 'Headphones', color: '#ec4899', code: 'CC' },
  'Applications': { icon: 'AppWindow', color: '#6366f1', code: 'APP' },
  'Endpoint': { icon: 'Monitor', color: '#14b8a6', code: 'END' }
};

/**
 * Hook for fetching and managing tech stack data
 * @param {Object} options - Hook options
 * @param {number} options.departmentId - Department ID to fetch data for
 * @param {string} options.view - Current view filter (full-stack, security, cloud, data, network)
 * @param {string} options.period - Time period filter (last-30-days, last-90-days, last-6-months, last-year, last-2-years, last-5-years, ytd, all-time)
 * @returns {Object} Tech stack data, loading state, error, and refetch function
 */
const useTechStack = ({ departmentId, view = 'full-stack', period = DEFAULT_PERIOD } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const timer = logger.perf.time('TechStack data fetch');

    try {
      logger.api.request('GET', '/api/tech-stack/analysis');

      // Build params - department_id is optional (null = all departments)
      const params = { view, period };
      if (departmentId) {
        params.department_id = departmentId;
      }

      const response = await apiClient.get('/api/tech-stack/analysis', { params });

      // Filter domains based on view
      if (view !== 'full-stack' && response.domains) {
        const viewFilter = view.toLowerCase();
        response.domains = response.domains.filter(d =>
          d.name.toLowerCase().includes(viewFilter) ||
          d.code.toLowerCase().includes(viewFilter)
        );
      }

      setData(response);
      logger.component.info('TechStack', 'Data loaded', {
        domains: response.domains?.length,
        vendors: response.summary?.totalVendors
      });
    } catch (err) {
      logger.error.error('TechStack', 'load_failed', { error: err.message });
      setError(err.message);
      setData(null);
    } finally {
      timer.end();
      setLoading(false);
    }
  }, [departmentId, view, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};

export default useTechStack;
export { DOMAIN_CONFIG, DOMAIN_MAPPING };
